import type {
  PortBlockRule,
  ResponseError,
  ScannerDetails,
  ScannerDetailsTypeEnum,
  ScannerPropertiesErrorCode,
  ScannerStatus,
  ScannerHostIdentificationScanningIntensity,
} from '@rtkwlf/rendall';
import { ScannerConnectionStatus } from '@rtkwlf/rendall';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import { useScannerDetails } from '@apis/rendall/queries/use-scanner-details/use-scanner-details';

type Options<T> = Omit<
  UseQueryOptions<ScannerDetails, ResponseError, T>,
  'queryKey' | 'queryFn' | 'select'
> & {
  select?: (data: ScannerDetails) => T;
};

export const useEnhancedScannerDetails = <T = ScannerDetails>(
  scannerId: string,
  options?: Options<T>,
): UseQueryResult<
  (T & ScannerDetailsComputedProperties) | undefined,
  ResponseError
> => {
  const { select, ...restOptions } = options || {};
  const enhancedSelect = (
    data: ScannerDetails,
  ): T & ScannerDetailsComputedProperties => {
    const selectedData = select ? select(data) : (data as T);

    const computedProperties: ScannerDetailsComputedProperties = {
      isConnected:
        data.connectionStatus?.toLowerCase() ===
        ScannerConnectionStatus.Connected,
    };

    return {
      ...selectedData,
      ...computedProperties,
    };
  };
  return useScannerDetails(scannerId, {
    ...restOptions,
    select: enhancedSelect,
  });
};

export type ScanExclusionType = { ipRange: string; ports: string };
type ScannerDetailsComputedProperties = {
  isConnected: boolean;
};

export type ScannerDetailsWithComputedProperties = ScannerDetails &
  ScannerDetailsComputedProperties;

export type ScannerDetailWithComputedProperties = ScannerDetail &
  ScannerDetailsComputedProperties;

export type ScannerDetail = {
  id: string;
  ip: string;
  netmask: string;
  denyList?: Array<string>;
  dnsServers?: Array<string>;
  CGIScanning: boolean;
  bruteForceChecks: boolean;
  hostIdentificationScans: boolean;
  onlyPingTheTarget: boolean;
  vulnerabilityScans: boolean;
  capabilities: Array<string>;
  name: string;
  type: ScannerDetailsTypeEnum;
  version: string;
  scannerStatus: ScannerStatus;
  connectionStatus: ScannerConnectionStatus;
  portBlockRules: Array<ScanExclusionType>;
  deploymentID: string;
  errorCode: ScannerPropertiesErrorCode;
  hostIdentificationScanIntensity: ScannerHostIdentificationScanningIntensity;
  hostIdentificationScanPeriod: number;
  maxConcurrentVulnerabilityScans: number;
  lastOpenVASScanTimestamp: string;
  gateway: string;
};

const getScannerSetting = (setting: string, input: ScannerDetails): boolean =>
  input.settings.filter((set) => set.name === setting)[0]?.enabled || false;

const getPortBlockRules = (
  portBlockRules: PortBlockRule[],
): Array<ScanExclusionType> =>
  portBlockRules.map(({ target, ports }) => ({
    ipRange: target,
    ports: ports.join(', '),
  }));

export const scannerDetailSelect = (input: ScannerDetails): ScannerDetail => {
  const scanner: ScannerDetail = {
    CGIScanning: getScannerSetting('CGIScanning', input),
    bruteForceChecks: getScannerSetting('bruteForceChecks', input),
    capabilities: input.capabilities,
    connectionStatus: input.connectionStatus,
    denyList: input.denyList,
    dnsServers: input.dnsServers,
    hostIdentificationScans: getScannerSetting(
      'hostIdentificationScans',
      input,
    ),
    id: input.id,
    ip: input.ip,
    name: input.name,
    netmask: input.netmask,
    onlyPingTheTarget: getScannerSetting('onlyPingTheTarget', input),
    scannerStatus: input.scannerStatus,
    type: input.type,
    version: input.version,
    vulnerabilityScans: getScannerSetting('vulnerabilityScans', input),
    portBlockRules: getPortBlockRules(input.portBlockRules),
    deploymentID: input.deploymentID,
    errorCode: input.errorCode,
    hostIdentificationScanIntensity: input.hostIdentificationScanIntensity,
    hostIdentificationScanPeriod: input.hostIdentificationScanPeriod,
    maxConcurrentVulnerabilityScans: input.maxConcurrentVulnerabilityScans,
    lastOpenVASScanTimestamp: input.lastOpenVASScanTimestamp,
    gateway: input.gateway,
  };

  return scanner;
};
